import { useEffect, useState } from 'react'
import styles from './Account.module.scss'
import {
    ScoopText,
    ScoopSelector,
    ScoopScreenLayout,
    ScoopAlert,
    ScoopSpinner,
    ScoopTab,
} from '../../ui'
import { TextAaIcon, TrashIcon, GearIcon } from '@phosphor-icons/react'
import { useWorkspaces } from '../../../api/hooks/workspaces/useWorkspaces'
import { updateWorkspaceName } from '../../../store/actions/authActions'
import { setResetTrigger } from '../../../store/actions/uiActions'
import { useDispatch } from 'react-redux'
import { useAccount } from '../../../api/hooks/account/useAccount'
import { useModal, MODALS } from '../../modals'
import { getWorkspaceID } from '../../../utils'
import { useUserInfo } from '../../../api/hooks/user/useUserInfo'
import { useLocation } from 'react-router-dom'
import { useWorkspaceMetadata } from '../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { AIProviderSettings } from './AIProviderSettings/AIProviderSettings'
import { WorkspaceSettings } from './WorkspaceSettings/WorkspaceSettings'
import { BillingSettings } from './BillingSettings/BillingSettings'
import { TeamSettings } from './TeamSettings/TeamSettings'

export const Account = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const workspaceID = getWorkspaceID()
    const [selectedTab, setSelectedTab] = useState(location.state?.selectedTab || 'team')
    const [selectedWorkspace, setSelectedWorkspace] = useState(
        location.state?.selectedWorkspace || null
    )
    const [loadingChangeRole, setLoadingChangeRole] = useState(null)

    const editWorkspaceModal = useModal(MODALS.ACCOUNT.EDIT_WORKSPACE)
    const deleteWorkspaceModal = useModal(MODALS.ACCOUNT.DELETE_WORKSPACE)
    const deleteModal = useModal(MODALS.COMMON.DELETE)

    const { user } = useUserInfo()
    const { workspaces } = useWorkspaces()
    const { workspaceMetadata } = useWorkspaceMetadata()
    const {
        members,
        loadingMembers,
        accountMembers,
        loadingAccountMembers,
        addUser,
        isAddingUser,
        removeUser,
        isRemovingUser,
        changeRole,
        workspaceData,
        updateWeekStartDay,
        isUpdatingWeekStartDay,
    } = useAccount(selectedWorkspace, {
        includeAccountMembers: true,
        accountID: user?.accountID,
    })

    useEffect(() => {
        if (workspaces.length > 0 && !selectedWorkspace) {
            setSelectedWorkspace(workspaces[0].workspaceID)
        }
    }, [workspaces])

    const getWorkspaceName = (workspaceID) => {
        const workspace = workspaces.find((workspace) => workspace.workspaceID === workspaceID)
        return workspace ? workspace.name : ''
    }

    const handleRenameWorkspace = async () => {
        const result = await editWorkspaceModal.show({
            title: 'Rename workspace',
            buttonText: 'Rename Workspace',
            initialName: getWorkspaceName(selectedWorkspace),
            workspaceID: selectedWorkspace,
            isCreate: false,
            onSuccess: (newWorkspace, response) => {
                if (newWorkspace.workspaceID === workspaceID) {
                    dispatch(setResetTrigger())
                    dispatch(
                        updateWorkspaceName(
                            newWorkspace.workspaceName,
                            newWorkspace.workspaceID,
                            response
                        )
                    )
                }
                setSelectedWorkspace(newWorkspace.workspaceID)
            },
        })
    }

    const handleDeleteWorkspace = async () => {
        const result = await deleteWorkspaceModal.show({
            workspaceName: getWorkspaceName(selectedWorkspace),
            workspaceID: selectedWorkspace,
        })
        if (result) {
            setSelectedWorkspace(workspaces[0].workspaceID)
        }
    }

    const BUTTONS = [
        {
            icon: <TextAaIcon size={20} weight="regular" />,
            label: 'Rename',
            onClick: handleRenameWorkspace,
        },
        {
            icon: <TrashIcon size={20} weight="regular" />,
            label: 'Delete',
            onClick: handleDeleteWorkspace,
        },
    ]

    const handleChangeWorkspace = (workspaceID) => {
        setSelectedWorkspace(workspaceID)
    }

    const handleRemoveUserClick = async (member) => {
        const confirmed = await deleteModal.show({
            title: member.userName,
            description: `Are you sure you want to remove ${member.userName} from ${getWorkspaceName(selectedWorkspace)}?`,
        })
        if (confirmed) {
            removeUser({
                workspaceID: selectedWorkspace,
                userID: member.userID,
                onSuccess: () => {},
            })
        }
    }

    const handleRoleChange = (role, member) => {
        setLoadingChangeRole(member.userID)
        changeRole({
            workspaceID: selectedWorkspace,
            userID: member.userID,
            role: role,
            onSuccess: () => setTimeout(() => setLoadingChangeRole(null), 1500),
        })
    }

    const getRole = () => {
        return members.find((member) => member.email === user?.userName)?.role
    }

    const columns = [
        {
            label: 'Name',
            key: 'name',
            sortable: true,
            render: (member) => <ScoopText>{member.userName}</ScoopText>,
        },
        {
            label: 'Email',
            key: 'email',
            sortable: true,
            render: (member) => <ScoopText>{member.email}</ScoopText>,
        },
        {
            label: 'Role',
            key: 'role',
            sortable: true,
            render: (member) => (
                <>
                    {loadingChangeRole === member.userID ? (
                        <ScoopSpinner />
                    ) : ['admin', 'owner'].includes(getRole()?.toLowerCase()) ? (
                        <ScoopSelector
                            value={member.role}
                            onChange={(e) => handleRoleChange(e.target.value, member)}
                            options={[
                                { label: 'Admin', value: 'Admin' },
                                { label: 'Owner', value: 'Owner' },
                                { label: 'Member', value: 'Member' },
                            ]}
                            size="sm"
                            removeMargin
                        />
                    ) : (
                        <ScoopText>{member.role}</ScoopText>
                    )}
                </>
            ),
        },
        {
            label: '',
            key: 'actions',
            sortable: false,
            render: (member, index) =>
                ['Admin', 'Owner'].includes(getRole()) ? (
                    <button
                        onClick={() => handleRemoveUserClick(member)}
                        aria-label="Remove user"
                        className={styles.iconButton}
                    >
                        <TrashIcon size={20} weight="regular" />
                    </button>
                ) : null,
        },
    ]

    // Check if BYOK is enabled from workspace metadata
    const byokEnabled = workspaceMetadata?.account?.limits?.byokEnabled ?? false

    const tabs = [
        { key: 'team', label: 'Team' },
        { key: 'workspaces', label: 'Workspaces' },
        ...(byokEnabled ? [{ key: 'ai-provider', label: 'AI Provider' }] : []),
        { key: 'billing', label: 'Billing & Plan' },
    ]

    const renderTabContent = () => {
        switch (selectedTab) {
            case 'team':
                return <TeamSettings />
            case 'workspaces':
                return (
                    <WorkspaceSettings
                        getRole={getRole}
                        accountMembers={accountMembers}
                        loadingAccountMembers={loadingAccountMembers}
                        addUser={addUser}
                        isAddingUser={isAddingUser}
                        columns={columns}
                        members={members}
                        loadingMembers={loadingMembers}
                        workspaceData={workspaceData}
                        updateWeekStartDay={updateWeekStartDay}
                        isUpdatingWeekStartDay={isUpdatingWeekStartDay}
                        workspaces={workspaces}
                        selectedWorkspace={selectedWorkspace}
                        handleChangeWorkspace={handleChangeWorkspace}
                        BUTTONS={BUTTONS}
                    />
                )
            case 'billing':
                return <BillingSettings workspaceMetadata={workspaceMetadata} />
            case 'ai-provider':
                if (!['owner', 'admin'].includes(user?.accountRole)) {
                    return (
                        <ScoopAlert severity="warning" variant="default">
                            Only account owners and admins can configure AI provider settings.
                        </ScoopAlert>
                    )
                }
                return <AIProviderSettings accountID={user?.accountID} />
            default:
                return null
        }
    }

    return (
        <>
            <ScoopScreenLayout
                title="Account Settings"
                description="Here you can add members your team, update your preferences and manage your Scoop Plan."
                icon={<GearIcon size={28} className={styles.headerIcon} />}
                contentClassName={styles.contentArea}
            >
                <div className={styles.mainContent}>
                    <ScoopTab
                        tabs={tabs}
                        value={selectedTab}
                        onChange={(e, newValue) => setSelectedTab(newValue)}
                    />
                    <div className={styles.tabContent}>{renderTabContent()}</div>
                </div>
            </ScoopScreenLayout>
        </>
    )
}
